import React, {CSSProperties, createElement, isValidElement} from "react"
import {useIntl} from "@irati/utils/lang"
import {extend, groupBy} from "lodash"
import SimpleMarkdown from "simple-markdown"
import {Body, BodyProps, H1, H2, H3, H4, H5, H6} from "@irati/ui/src/Typography"
import theme from "@irati/ui/src/global/theme"
import Expander from "../Expander"
import {StyledExpanderIcon, HeaderLabel} from "./styled"
import isContentKey from "@irati/utils/lang/helpers/isContentKey"

interface ComponentProps {
  body?: Pick<BodyProps, "small" | "large">
}

type Components = Record<string, JSX.Element>

const anchorStyles: CSSProperties = {
  color: theme.colors.primary1,
  fontFamily: theme.font.bold
}

const headings = [H1, H2, H3, H4, H5, H6]
const headerRule = extend({}, SimpleMarkdown.defaultRules.heading, {
  react: (node, output, state) => {
    const Heading = headings[node.level - 1] ?? H3
    return (
      <Heading key={state.key} marginBottom={theme.spacing.oneAndHalf}>
        {output(node.content)}
      </Heading>
    )
  }
})

/**
 * capture phrase blocks that are words (see regex `\w`) of at least one character inside of `{}`
 *
 * @example
 * hey {there} world // valid
 * hey { there} world // not valid due to space
 * hey {the-re} world // not valid due to dash
 * hey {the_re} world // valid as `_` is part of word
 */
const interpolationRule = (components: Components) =>
  extend({}, SimpleMarkdown.defaultRules.heading, {
    match: function (source: string) {
      return /^\{(\w+)\}/.exec(source)
    },
    parse: function (capture, parse, state) {
      return {
        content: parse(capture[1], state)
      }
    },
    react: function (node, output, state) {
      const [key] = output(node.content)
      const component = components[key]
      return component ? <React.Fragment key={state.key}>{component}</React.Fragment> : null
    }
  })

const listRule = extend({}, SimpleMarkdown.defaultRules.list, {
  react: (node, output, state) => {
    return createElement(
      node.ordered ? "ol" : "ul",
      {
        key: state.key,
        start: node.start // numbers for 'ol', means users can start higher than 1 if desired
      },
      node.items.map((item, i) => (
        <li key={state.key + i}>
          <Body>{output(item, state)}</Body>
        </li>
      ))
    )
  }
})

const paragraphRule = (paraProps: ComponentProps["body"]) =>
  extend({}, SimpleMarkdown.defaultRules.paragraph, {
    react: (node, output, state) => {
      return (
        <Body key={state.key} marginBottom={theme.spacing.oneAndHalf} {...paraProps}>
          {output(node.content)}
        </Body>
      )
    }
  })

const anchorRule = extend({}, SimpleMarkdown.defaultRules.link, {
  react: (node, output, state) => {
    const props = {
      key: state.key,
      href: SimpleMarkdown.sanitizeUrl(node.target),
      title: node.title,
      target: "_blank",
      style: anchorStyles
    }
    return <a {...props}>{output(node.content)}</a>
  }
})

const strongRule = extend({}, SimpleMarkdown.defaultRules.strong, {
  react: (node, output, state) => {
    return (
      <Body as="strong" key={state.key} weight="bold">
        {output(node.content, state)}
      </Body>
    )
  }
})

const expandRule = {
  order: SimpleMarkdown.defaultRules.codeBlock.order + 0.5,
  match: (source) => /^\|:(.+)\n([\s\S]+?):\|/.exec(source),
  parse: (capture, parse, state) => {
    return {
      buttonText: capture[1],
      content: parse(`${capture[2]}\n\n`, state)
    }
  },
  react: (node, output, state) => {
    return (
      <div key={state.key}>
        <Expander showText={node.buttonText} hideText={node.buttonText} Arrow={StyledExpanderIcon} HeaderLabel={HeaderLabel}>
          {output(node.content, {inline: false})}
        </Expander>
      </div>
    )
  }
}

const rules = (props: ComponentProps, components: Components) =>
  extend({}, SimpleMarkdown.defaultRules, {
    link: anchorRule,
    strong: strongRule,
    expand: expandRule,
    paragraph: paragraphRule(props.body),
    heading: headerRule,
    list: listRule,
    interpolationRule: interpolationRule(components)
  })

const parser = (props: ComponentProps, components: Components) => SimpleMarkdown.parserFor(rules(props, components))
const reactOutput = (props: ComponentProps, components: Components) => SimpleMarkdown.outputFor(rules(props, components), "react")

export interface MarkdownProps {
  source: string
  className?: string
  /**
   * override individual component styles where appropriate
   *
   * The available props are a slimmed down selection as we want to be mindful the goal
   * is to create a Markdown component that won't require code changes in future ~if~ when
   * product decide to re-write this markup
   */
  componentProps?: ComponentProps
  /**
   * a record of key value pairs where keys are part of a phrase block
   * values will be interpolated into the placeholders
   *
   * @example
   * // with nested text
   * const props: MarkdownProps = {
   *   source: "hello {name}",
   *   values: {
   *     name: "Terry"
   *   }
   * }
   * //  outputs <p>hello terry</p>
   *
   * // with a nested component
   * const props: MarkdownProps = {
   *   source: "Basket: {price}",
   *   values: {
   *     price: <FormattedCurrency amount={100} />
   *   }
   * }
   * // outputs
   * (<p>Basket:
   *     <FormattedCurrency amount={100} />
   * </p>)
   */
  values?: Record<string, number | string | JSX.Element>
}

function Markdown({componentProps = {}, source, values = {}, className}: MarkdownProps) {
  const {formatMessage} = useIntl()
  const {text, components} = splitValues(values ?? {})

  const textSource = isContentKey(source) ? formatMessage({id: source}, text) : source

  // @notes: Many rules require content to end in \n\n to be interpreted as a block. (notes from simple-markdown)
  const blockSource = `${textSource || ""}\n\n`

  const parseTree = parser(componentProps, components)(blockSource, {inline: false})
  return <div className={className}>{reactOutput(componentProps, components)(parseTree)}</div>
}

/**
 * splitValues takes the `values` object passed to Markdown and separates values
 * that are primitive values (strings and numbers) from components.
 */
function splitValues(values: Pick<MarkdownProps, "values">) {
  const groups: any = groupBy(Object.entries(values), ([_, value]) => (isValidElement(value) ? "component" : "text"))

  // There are some type assertions here to get around the grouping confusing typescript
  const text: Record<string, number | string> = Object.fromEntries(groups.text ?? [])
  const components: Components = Object.fromEntries(groups.component ?? [])

  // because we have remove the component keys, phrase will log an error about missing keys
  // this puts in replacements that will leave them unchanged, i.e `foo {bar}` will just become `foo {bar}`
  // the components are then interpolated in the markdown itself
  const fallbackKeys = Object.fromEntries(Object.keys(components).map((phraseKey) => [phraseKey, `{${phraseKey}}`]))

  return {
    text: {
      ...text,
      ...fallbackKeys
    },
    components
  }
}

export default Markdown
